<template>
  <div class="estudios-page">
    <div class="estudios-header">
      <Button icon="pi pi-arrow-left" text rounded severity="secondary" @click="volver" />
      <div class="flex-1">
        <div class="estudios-title">Estudios iniciados</div>
        <div class="estudios-sub">{{ estudios.length }} estudio(s) en total</div>
      </div>
      <div class="flex gap-2 flex-wrap justify-content-end">
        <Button
          icon="pi pi-users"
          :label="`Sin informar (${gruposSinInformar.length})`"
          size="small"
          severity="secondary"
          outlined
          @click="showPendientesDialog = true"
        />
        <Button
          icon="pi pi-chart-bar"
          label="Estadísticas"
          size="small"
          severity="secondary"
          outlined
          @click="showStatsDialog = true"
        />
        <Button
          icon="pi pi-map"
          :label="vistaMapa ? 'Ocultar mapa' : `Mostrar mapa (${estudiosConUbicacion.length})`"
          size="small"
          :severity="vistaMapa ? 'info' : 'secondary'"
          :outlined="!vistaMapa"
          @click="vistaMapa = !vistaMapa"
        />
        <Button
          icon="pi pi-link"
          label="Enlace por grupo"
          size="small"
          severity="secondary"
          outlined
          @click="openLinkDialog"
        />
        <Button
          icon="pi pi-plus"
          label="Nuevo estudio"
          size="small"
          @click="abrirNuevoEstudio"
        />
      </div>
    </div>

    <!-- Búsqueda de texto libre sobre todos los datos del estudio -->
    <IconField iconPosition="left" class="mb-2 busqueda-estudios">
      <InputIcon class="pi pi-search" />
      <InputText
        v-model="busqueda"
        placeholder="Buscar en todos los datos (nombre, teléfono, dirección, observaciones...)"
        class="w-full"
      />
    </IconField>

    <!-- Filtros -->
    <div class="flex align-items-center gap-2 mb-2 flex-wrap">
      <label class="text-sm text-color-secondary">Filtrar</label>
      <Dropdown
        v-model="filtroTerritorioId"
        :options="territorioOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Todos los territorios"
        filter
        showClear
        class="w-15rem"
      />
      <Dropdown
        v-model="filtroGrupoId"
        :options="guestGroupOptions"
        optionLabel="label"
        optionValue="id"
        placeholder="Todos los grupos"
        filter
        showClear
        class="w-15rem"
      />
      <Dropdown
        v-model="filtroZonaId"
        :options="zonaOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Todas las congregaciones"
        filter
        showClear
        class="w-15rem"
      />
      <MultiSelect
        v-model="filtroUrgencia"
        :options="URGENCIA_NIVELES_FILTRO"
        optionLabel="label"
        optionValue="value"
        placeholder="Toda urgencia"
        :maxSelectedLabels="1"
        showClear
        class="w-12rem"
      />
      <Dropdown
        v-model="filtroTipo"
        :options="TIPO_OPCIONES"
        optionLabel="label"
        optionValue="value"
        placeholder="Tipo"
        showClear
        class="w-14rem"
      />
      <Dropdown
        v-model="filtroAsignado"
        :options="SI_NO_OPCIONES"
        optionLabel="label"
        optionValue="value"
        placeholder="Asignado"
        showClear
        class="w-10rem"
      />
      <Dropdown
        v-model="filtroAtendiendose"
        :options="SI_NO_OPCIONES"
        optionLabel="label"
        optionValue="value"
        placeholder="Atendiéndose"
        showClear
        class="w-10rem"
      />
    </div>
    <div class="flex align-items-center gap-2 mb-3 flex-wrap">
      <div class="text-sm text-color-secondary flex-1">
        Mostrando {{ estudiosFiltrados.length }} de {{ estudios.length }} estudio(s)
        — {{ estudiosConUbicacion.length }} con ubicación marcada — tocá un dato para editarlo
      </div>
      <label class="text-sm text-color-secondary" for="filas-por-pagina">Tarjetas por página</label>
      <Dropdown
        inputId="filas-por-pagina"
        :modelValue="filasPorPagina"
        :options="FILAS_PAGINA_OPCIONES"
        class="w-6rem"
        @update:modelValue="cambiarFilasPorPagina"
      />
    </div>

    <div class="estudios-layout" :class="{ 'con-mapa': vistaMapa }">
    <!-- Mapa general: mismos estudios filtrados que las tarjetas, sólo los que tienen ubicación -->
    <div v-if="vistaMapa" class="estudios-mapa-panel" :class="{ 'mapa-pantalla-completa': mapaPantallaCompleta }">
      <div ref="estudiosMapDiv" class="estudios-mapa-vista" />
      <SelectButton
        v-model="filtroAsignado"
        :options="FILTRO_ASIGNADO_MAPA"
        optionLabel="label"
        optionValue="value"
        :allowEmpty="false"
        class="mapa-filtro-asignado"
      />
      <div class="mapa-acciones">
        <Button
          size="small"
          severity="secondary"
          :icon="sateliteEstudios ? 'pi pi-map' : 'pi pi-globe'"
          :label="sateliteEstudios ? 'Mapa' : 'Satélite'"
          @click="sateliteEstudios = !sateliteEstudios"
        />
        <Button
          size="small"
          :severity="mapaLimpio ? 'info' : 'secondary'"
          icon="pi pi-filter-slash"
          v-tooltip.left="mapaLimpio ? 'Mostrar negocios y lugares' : 'Ocultar negocios y lugares (sólo calles)'"
          @click="mapaLimpio = !mapaLimpio"
        />
        <Button
          size="small"
          severity="secondary"
          :icon="mapaPantallaCompleta ? 'pi pi-window-minimize' : 'pi pi-window-maximize'"
          v-tooltip.left="mapaPantallaCompleta ? 'Salir de pantalla completa (Esc)' : 'Ver mapa en pantalla completa'"
          @click="mapaPantallaCompleta = !mapaPantallaCompleta"
        />
      </div>
    </div>

    <!-- Tarjetas de estudios: muestran todos los datos y cada dato se edita tocándolo -->
    <div class="estudios-lista">
      <div v-if="cargando && !estudios.length" class="text-500 text-sm">Cargando estudios...</div>
      <div v-else-if="!estudiosFiltrados.length" class="text-500 text-sm">Sin estudios registrados</div>
      <div v-else class="estudios-cards">
        <div
          v-for="e in estudiosPagina"
          :id="`estudio-${e.id}`"
          :key="e.id"
          class="estudio-card"
          :class="{ 'estudio-card-resaltada': resaltadoId === e.id }"
          :style="{ '--urg-color': urgenciaInfo(e.urgencia)?.color || 'var(--surface-border, #e5e7eb)' }"
        >
          <div class="card-head">
            <span class="estudio-numero" v-tooltip="'Número del punto en el mapa'">{{ numeroEstudio.get(e.id) }}</span>
            <template v-for="c in CAMPOS_CABECERA" :key="c.field">
              <Dropdown
                v-if="estaEditando(e, c.field)"
                :ref="setDropdownEditor"
                v-model="edicion.valor"
                :options="opcionesCampo(c)"
                optionLabel="label"
                :optionValue="c.optionValue || 'value'"
                :placeholder="c.placeholder"
                :showClear="c.showClear"
                class="card-head-editor"
                @change="confirmarEdicion"
                @hide="descartarEdicionSelect(c.field)"
              />
              <button v-else type="button" class="card-head-btn" v-tooltip="`${c.label} — tocá para cambiar`" @click="empezarEdicion(e, c.field)">
                <span
                  v-if="c.field === 'urgencia'"
                  class="urgencia-badge"
                  :class="{ 'urgencia-badge-vacia': !e.urgencia }"
                  :style="{ backgroundColor: urgenciaInfo(e.urgencia)?.color }"
                >{{ e.urgencia || '–' }}</span>
                <Tag v-else :severity="tipoInfo(e.tipo).severity" :value="tipoInfo(e.tipo).label" />
              </button>
            </template>
            <div class="flex-1" />
            <Button icon="pi pi-copy" text rounded size="small" severity="success" v-tooltip="'Copiar mensaje para WhatsApp'" @click="abrirCopiarMensaje(e)" />
            <Button icon="pi pi-eye" text rounded size="small" severity="secondary" v-tooltip="'Ver detalle y notas'" @click="verDetalle(e)" />
            <Button icon="pi pi-pencil" text rounded size="small" severity="secondary" v-tooltip="'Editar en formulario (ubicación en el mapa)'" @click="editarEstudio(e)" />
            <Button icon="pi pi-share-alt" text rounded size="small" severity="secondary" v-tooltip="'Compartir con el asignado'" @click="openAsignacionLinkDialog(e)" />
          </div>

          <div class="card-campos">
            <div v-for="c in CAMPOS_TARJETA" :key="c.field" class="campo" :class="{ 'campo-ancho': c.ancho }">
              <div class="detalle-label">{{ c.label }}</div>

              <!-- Editor del dato -->
              <template v-if="estaEditando(e, c.field)">
                <Dropdown
                  v-if="c.tipo === 'select'"
                  :ref="setDropdownEditor"
                  v-model="edicion.valor"
                  :options="opcionesCampo(c)"
                  optionLabel="label"
                  :optionValue="c.optionValue || 'value'"
                  :placeholder="c.placeholder"
                  filter
                  showClear
                  class="w-full"
                  @change="confirmarEdicion"
                  @hide="descartarEdicionSelect(c.field)"
                />
                <Textarea
                  v-else-if="c.tipo === 'multilinea'"
                  v-model="edicion.valor"
                  v-focus
                  rows="3"
                  autoResize
                  class="w-full"
                  @blur="confirmarEdicion"
                  @keydown.esc="descartarEdicion"
                  @keydown.enter.ctrl="confirmarEdicion"
                  @keydown.enter.meta="confirmarEdicion"
                />
                <InputText
                  v-else
                  v-model="edicion.valor"
                  v-focus
                  :type="c.tipo === 'date' ? 'date' : 'text'"
                  class="w-full"
                  @blur="confirmarEdicion"
                  @keydown.enter="confirmarEdicion"
                  @keydown.esc="descartarEdicion"
                />
              </template>

              <!-- Valor (tocar para editar) -->
              <div
                v-else
                class="campo-valor"
                :class="{ 'campo-obs': c.tipo === 'multilinea', 'font-medium': c.destacado }"
                tabindex="0"
                @click="empezarEdicion(e, c.field)"
                @keydown.enter.prevent="empezarEdicion(e, c.field)"
              >
                <span v-if="textoCampo(e, c)">{{ textoCampo(e, c) }}</span>
                <span v-else class="text-400">—</span>
                <span v-if="c.field === 'guestGroupId' && zonaNombre(zonaIdDeGrupo(e.guestGroupId))" class="text-sm text-500">
                  · {{ zonaNombre(zonaIdDeGrupo(e.guestGroupId)) }}
                </span>
                <a
                  v-if="c.telefono && e[c.field]"
                  href="#"
                  class="ml-2"
                  v-tooltip="'Enviar WhatsApp'"
                  @click.prevent.stop="abrirEnviarWhatsapp(e[c.field], estudioMensajeVars(e))"
                ><i class="pi pi-whatsapp" /></a>
                <a
                  v-if="c.field === 'direccion' && e.lat != null && e.lng != null"
                  :href="`https://www.google.com/maps?q=${e.lat},${e.lng}`"
                  target="_blank"
                  rel="noopener"
                  class="ml-2"
                  v-tooltip="'Ver en el mapa'"
                  @click.stop
                ><i class="pi pi-map-marker" /></a>
              </div>

              <!-- Ubicación en el mapa, debajo de la dirección -->
              <template v-if="c.field === 'direccion'">
                <div
                  v-if="e.lat != null && e.lng != null"
                  v-mini-mapa="[e.lat, e.lng]"
                  class="card-mapa"
                  v-tooltip="'Tocá para cambiar la ubicación'"
                  @click="abrirMapaEstudio(e)"
                />
                <Button
                  v-else-if="e.direccion"
                  icon="pi pi-search"
                  label="Buscar la dirección en el mapa"
                  text
                  size="small"
                  class="mt-1 px-1"
                  @click="abrirMapaEstudio(e)"
                />
                <Button
                  v-else
                  icon="pi pi-map-marker"
                  label="Marcar ubicación en el mapa"
                  text
                  size="small"
                  class="mt-1 px-1"
                  @click="abrirMapaEstudio(e)"
                />
              </template>
            </div>
          </div>

          <div class="card-foot">
            <label class="flex align-items-center gap-2 text-sm">
              <InputSwitch :modelValue="!!e.asignado" @update:modelValue="(v) => guardarCampos(e.id, { asignado: v })" />
              Asignado
            </label>
            <label class="flex align-items-center gap-2 text-sm">
              <InputSwitch :modelValue="!!e.atendiendose" @update:modelValue="(v) => guardarCampos(e.id, { atendiendose: v })" />
              Atendiéndose
            </label>
            <div class="flex-1" />
            <span
              v-if="e.adjuntos?.length"
              class="text-sm text-500 cursor-pointer"
              v-tooltip="e.adjuntos.map((a) => a.originalName).join('\n')"
              @click="verDetalle(e)"
            >
              <i class="pi pi-paperclip" /> {{ e.adjuntos.length }}
            </span>
          </div>
        </div>
      </div>
      <Paginator
        v-if="estudiosFiltrados.length > filasPorPagina"
        :first="primerIndice"
        :rows="filasPorPagina"
        :totalRecords="estudiosFiltrados.length"
        :rowsPerPageOptions="FILAS_PAGINA_OPCIONES"
        class="mt-3"
        @page="onPageChange"
      />
    </div>
    </div>

    <!-- Dialog: marcar ubicación en el mapa -->
    <Dialog
      v-model:visible="showMapDialog"
      header="Marcar ubicación del estudio"
      modal
      style="width: min(560px, 95vw)"
      @show="initPickerMap"
      @hide="destroyPickerMap"
    >
      <div class="flex flex-column gap-2">
        <small class="text-500">Buscá la dirección, tocá el mapa para marcar dónde se realiza el estudio, o pegá un link de Google Maps.</small>
        <div class="flex gap-2">
          <InputText
            v-model="pickerBusqueda"
            placeholder="Buscar dirección (calle, número, ciudad)"
            class="flex-1"
            @keyup.enter="buscarDireccionPicker"
          />
          <Button
            icon="pi pi-search"
            label="Buscar"
            :loading="pickerBuscando"
            :disabled="!pickerBusqueda.trim()"
            @click="buscarDireccionPicker"
          />
        </div>
        <small v-if="pickerBusquedaError" class="text-red-500">{{ pickerBusquedaError }}</small>
        <div v-if="pickerResultados.length > 1" class="flex flex-column gap-1">
          <small class="text-500">Encontramos varias ubicaciones, elegí la correcta:</small>
          <button
            v-for="(r, i) in pickerResultados"
            :key="i"
            type="button"
            class="resultado-geo"
            :class="{ 'resultado-geo-activo': pickerResultadoSel === r }"
            @click="elegirResultadoPicker(r)"
          ><i class="pi pi-map-marker" /> {{ r.label }}</button>
        </div>
        <small v-else-if="pickerResultados.length === 1" class="text-500">
          <i class="pi pi-check-circle text-green-500" /> {{ pickerResultados[0].label }}
        </small>
        <div class="flex gap-2">
          <InputText
            v-model="pickerLinkTexto"
            placeholder="Pegar link de Google Maps o coordenadas"
            class="flex-1"
            @keyup.enter="usarLinkPicker"
          />
          <Button
            label="Usar"
            :loading="pickerLinkResolving"
            :disabled="!pickerLinkTexto.trim()"
            @click="usarLinkPicker"
          />
        </div>
        <small v-if="pickerLinkError" class="text-red-500">{{ pickerLinkError }}</small>
        <div class="picker-map-wrap">
          <div ref="mapDiv" class="picker-map" />
          <Button
            class="mapa-capa-toggle"
            size="small"
            severity="secondary"
            :icon="sateliteEstudios ? 'pi pi-map' : 'pi pi-globe'"
            :label="sateliteEstudios ? 'Mapa' : 'Satélite'"
            @click="sateliteEstudios = !sateliteEstudios"
          />
        </div>
        <div class="flex gap-2 justify-content-end">
          <Button v-if="pickerOrigen.lat != null" label="Quitar marca" severity="danger" text @click="quitarMarca" />
          <Button label="Cancelar" severity="secondary" @click="showMapDialog = false" />
          <Button label="Usar esta ubicación" :disabled="!pickerLatLng" @click="confirmarMarca" />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: enlace público por grupo de invitados + envío por WhatsApp -->
    <Dialog v-model:visible="showLinkDialog" header="Enlace de estudios por grupo" modal style="width: min(520px, 95vw)">
      <div class="flex flex-column gap-3 pt-2">
        <small class="text-500">
          Cada grupo de invitados tiene su propio enlace. Quien lo reciba puede cargar estudios,
          ver los del grupo y editarlos, pero no eliminarlos.
        </small>
        <div class="flex flex-column gap-1">
          <label class="text-sm">Grupo de invitados</label>
          <Dropdown
            v-model="linkGroupId"
            :options="guestGroupOptions"
            optionLabel="label"
            optionValue="id"
            placeholder="Seleccioná un grupo"
            filter
            class="w-full"
          />
        </div>
        <template v-if="linkGroupId">
          <div v-if="linkLoading" class="text-500 text-sm">Cargando enlace...</div>
          <div v-else class="flex align-items-center gap-2">
            <InputText :modelValue="linkUrl" readonly class="w-full" />
            <Button
              :icon="linkCopied ? 'pi pi-check' : 'pi pi-copy'"
              :severity="linkCopied ? 'success' : 'secondary'"
              @click="copyLink"
            />
            <Button
              icon="pi pi-external-link"
              severity="secondary"
              v-tooltip.top="'Abrir en una nueva pestaña'"
              @click="openLinkInNewTab"
            />
          </div>
          <div v-if="plantillas.length" class="flex flex-column gap-1">
            <label class="text-sm">Plantilla</label>
            <Dropdown
              v-model="selectedPlantillaId"
              :options="plantillas"
              optionLabel="nombre"
              optionValue="id"
              class="w-full"
            />
          </div>
          <Textarea v-model="messageDraft" rows="6" autoResize />
          <div v-if="!linkGroup?.whatsapp" class="text-500 text-sm">
            El grupo no tiene WhatsApp cargado: se abrirá WhatsApp para que elijas el contacto.
          </div>
        </template>
        <div class="flex gap-2 justify-content-end">
          <Button label="Cerrar" severity="secondary" @click="showLinkDialog = false" />
          <Button
            icon="pi pi-whatsapp"
            label="Enviar por WhatsApp"
            :disabled="!linkGroupId || linkLoading"
            @click="sendLinkWhatsapp"
          />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: enlace individual de un estudio para el publicador asignado -->
    <Dialog v-model:visible="showAsignacionLinkDialog" header="Enlace para el publicador asignado" modal style="width: min(520px, 95vw)">
      <div class="flex flex-column gap-3 pt-2">
        <small class="text-500">
          Este enlace es de un solo estudio. Quien lo reciba ve los datos del estudio, puede completar
          quién se hace cargo (si todavía no estaba asignado) y agregar notas de seguimiento (visitas, resultados).
        </small>
        <div v-if="asignacionLinkLoading" class="text-500 text-sm">Cargando enlace...</div>
        <div v-else class="flex align-items-center gap-2">
          <InputText :modelValue="asignacionLinkUrl" readonly class="w-full" />
          <Button
            :icon="asignacionLinkCopied ? 'pi pi-check' : 'pi pi-copy'"
            :severity="asignacionLinkCopied ? 'success' : 'secondary'"
            @click="copyAsignacionLink"
          />
          <Button
            icon="pi pi-external-link"
            severity="secondary"
            v-tooltip.top="'Abrir en una nueva pestaña'"
            @click="openAsignacionLinkInNewTab"
          />
        </div>
        <div class="flex gap-2 justify-content-end">
          <Button label="Cerrar" severity="secondary" @click="showAsignacionLinkDialog = false" />
          <Button
            icon="pi pi-whatsapp"
            label="Enviar por WhatsApp"
            :disabled="asignacionLinkLoading"
            @click="sendAsignacionLinkWhatsapp"
          />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: detalle / edición de un estudio (un único modal) -->
    <Dialog v-model:visible="showEstudioDialog" :header="estudioDialogHeader" modal style="width: min(640px, 95vw)" @hide="cerrarEstudioDialog">
      <!-- Modo edición: alta o edición de un estudio -->
      <div v-if="modoEdicion" class="flex flex-column gap-3">
        <div class="grid">
          <div class="col-12 md:col-6 flex flex-column gap-1 mb-2">
            <label class="text-sm">Territorio</label>
            <Dropdown
              v-model="form.territorioId"
              :options="territorioOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Sin territorio"
              filter
              showClear
              class="w-full"
            />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1 mb-2">
            <label class="text-sm">Grupo de invitados</label>
            <Dropdown
              v-model="form.guestGroupId"
              :options="guestGroupOptions"
              optionLabel="label"
              optionValue="id"
              placeholder="Sin grupo asignado"
              filter
              showClear
              class="w-full"
            />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1 mb-2">
            <label class="text-sm">Publicador</label>
            <InputText v-model="form.publicador" placeholder="Nombre del publicador" />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1 mb-2">
            <label class="text-sm">WhatsApp del publicador <span class="text-500">(opcional)</span></label>
            <InputText v-model="form.whatsapp" placeholder="+54 9 264 555 5555" />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1 mb-2">
            <label class="text-sm">Estudiante</label>
            <InputText v-model="form.estudiante" placeholder="Nombre del estudiante" />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1 mb-2">
            <label class="text-sm">Teléfono del estudiante <span class="text-500">(opcional)</span></label>
            <InputText v-model="form.estudianteWhatsapp" placeholder="+54 9 264 555 5555" />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1 mb-2">
            <label class="text-sm">Asignado a <span class="text-500">(opcional)</span></label>
            <InputText v-model="form.asignadoA" placeholder="Quién hace el seguimiento" />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1 mb-2">
            <label class="text-sm">WhatsApp del asignado <span class="text-500">(opcional)</span></label>
            <InputText v-model="form.asignadoAWhatsapp" placeholder="+54 9 264 555 5555" />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1 mb-2">
            <label class="text-sm">Dirección / Ubicación</label>
            <div class="flex gap-2">
              <InputText v-model="form.direccion" placeholder="Dirección donde se realiza" class="flex-1" />
              <Button
                :icon="form.lat ? 'pi pi-map-marker' : 'pi pi-map'"
                :severity="form.lat ? 'success' : 'secondary'"
                outlined
                v-tooltip="form.lat ? 'Ubicación marcada — editar en el mapa' : 'Marcar en el mapa'"
                @click="openMapPicker"
              />
            </div>
            <div
              v-if="form.lat != null && form.lng != null"
              ref="miniMapDiv"
              class="mini-map mini-map-clickable"
              v-tooltip="'Tocá para editar la ubicación'"
              @click="openMapPicker"
            />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1 mb-2">
            <label class="text-sm">Fecha de inicio</label>
            <InputText v-model="form.fechaInicio" type="date" />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1 mb-2">
            <label class="text-sm">A quién se lo pasó</label>
            <InputText v-model="form.aQuienSePaso" placeholder="Si el estudio se pasó a otro publicador" />
          </div>
          <div class="col-12 flex flex-column gap-2 mb-2">
            <label class="text-sm">Urgencia</label>
            <div class="flex gap-2 flex-wrap">
              <button
                v-for="n in URGENCIA_NIVELES"
                :key="n.value"
                type="button"
                class="urgencia-chip"
                :class="{ 'urgencia-chip-active': form.urgencia === n.value }"
                :style="{ '--chip-color': n.color }"
                v-tooltip="n.label"
                @click="form.urgencia = form.urgencia === n.value ? null : n.value"
              >{{ n.value }}</button>
            </div>
          </div>
          <div class="col-12 md:col-4 flex flex-column gap-1 mb-2">
            <label class="text-sm">Tipo</label>
            <Dropdown
              v-model="form.tipo"
              :options="TIPO_OPCIONES"
              optionLabel="label"
              optionValue="value"
              class="w-full"
            />
          </div>
          <div class="col-12 md:col-4 flex align-items-center gap-2 mb-2">
            <InputSwitch v-model="form.asignado" />
            <span class="text-sm">Asignado</span>
          </div>
          <div class="col-12 md:col-4 flex align-items-center gap-2 mb-2">
            <InputSwitch v-model="form.atendiendose" />
            <span class="text-sm">Atendiéndose</span>
          </div>
          <div class="col-12 flex flex-column gap-1 mb-2">
            <label class="text-sm">Observaciones</label>
            <Textarea v-model="form.observaciones" rows="2" autoResize placeholder="Horarios, avances, detalles del estudio..." />
          </div>
        </div>
        <div v-if="errorGuardar" class="text-red-500 text-sm">{{ errorGuardar }}</div>
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" text @click="cancelarEdicion" />
          <Button :label="editandoId ? 'Guardar cambios' : 'Agregar'" icon="pi pi-check" :loading="guardando" @click="guardarEstudio" :disabled="!form.publicador && !form.estudiante" />
        </div>
      </div>

      <!-- Modo vista: detalle de un estudio existente -->
      <div v-else-if="detalleEstudio" class="flex flex-column gap-3">
        <div class="flex gap-2 flex-wrap">
          <Tag
            v-if="detalleEstudio.urgencia"
            :style="{ backgroundColor: urgenciaInfo(detalleEstudio.urgencia)?.color, color: '#fff' }"
            :value="`Urgencia: ${urgenciaInfo(detalleEstudio.urgencia)?.label}`"
          />
          <Tag :severity="tipoInfo(detalleEstudio.tipo).severity" :value="tipoInfo(detalleEstudio.tipo).label" />
          <Tag v-if="detalleEstudio.asignado" severity="secondary" value="Asignado" />
          <Tag v-if="detalleEstudio.atendiendose" severity="success" value="Atendiéndose" />
          <Tag v-if="detalleEstudio.territorioNombre" severity="secondary" :value="detalleEstudio.territorioNombre" />
          <Tag v-if="guestGroupLabel(detalleEstudio.guestGroupId)" severity="info" :value="guestGroupLabel(detalleEstudio.guestGroupId)" />
          <Tag v-if="detalleEstudio.fechaInicio" severity="secondary" :value="`Inicio: ${detalleEstudio.fechaInicio}`" />
        </div>

        <div class="grid">
          <div class="col-12 md:col-6">
            <div class="detalle-label">Estudiante</div>
            <div class="detalle-value">
              {{ detalleEstudio.estudiante || '-' }}
              <a
                v-if="detalleEstudio.estudianteWhatsapp"
                href="#"
                class="ml-2"
                v-tooltip="`WhatsApp: ${detalleEstudio.estudianteWhatsapp}`"
                @click.prevent="abrirEnviarWhatsapp(detalleEstudio.estudianteWhatsapp, estudioMensajeVars(detalleEstudio))"
              >
                <i class="pi pi-whatsapp" />
              </a>
            </div>
          </div>
          <div class="col-12 md:col-6">
            <div class="detalle-label">Publicador</div>
            <div class="detalle-value">
              {{ detalleEstudio.publicador || '-' }}
              <a
                v-if="detalleEstudio.whatsapp"
                href="#"
                class="ml-2"
                v-tooltip="`WhatsApp: ${detalleEstudio.whatsapp}`"
                @click.prevent="abrirEnviarWhatsapp(detalleEstudio.whatsapp, estudioMensajeVars(detalleEstudio))"
              >
                <i class="pi pi-whatsapp" />
              </a>
            </div>
          </div>
          <div v-if="detalleEstudio.asignadoA" class="col-12 md:col-6">
            <div class="detalle-label">Asignado a</div>
            <div class="detalle-value">
              {{ detalleEstudio.asignadoA }}
              <a
                v-if="detalleEstudio.asignadoAWhatsapp"
                href="#"
                class="ml-2"
                v-tooltip="`WhatsApp: ${detalleEstudio.asignadoAWhatsapp}`"
                @click.prevent="abrirEnviarWhatsapp(detalleEstudio.asignadoAWhatsapp, estudioMensajeVars(detalleEstudio))"
              >
                <i class="pi pi-whatsapp" />
              </a>
            </div>
          </div>
          <div class="col-12">
            <div class="detalle-label">Dirección / Ubicación</div>
            <div class="detalle-value">
              {{ detalleEstudio.direccion || '-' }}
              <a
                v-if="detalleEstudio.lat && detalleEstudio.lng"
                :href="`https://www.google.com/maps?q=${detalleEstudio.lat},${detalleEstudio.lng}`"
                target="_blank"
                rel="noopener"
                class="ml-2"
                v-tooltip="'Ver en el mapa'"
              >
                <i class="pi pi-map-marker" />
              </a>
            </div>
          </div>
          <div v-if="detalleEstudio.lat != null && detalleEstudio.lng != null" class="col-12">
            <div ref="miniMapDiv" class="mini-map" />
          </div>
          <div v-if="detalleEstudio.aQuienSePaso" class="col-12">
            <div class="detalle-label">A quién se lo pasó</div>
            <div class="detalle-value">{{ detalleEstudio.aQuienSePaso }}</div>
          </div>
          <div v-if="detalleEstudio.observaciones" class="col-12">
            <div class="detalle-label">Observaciones</div>
            <div class="detalle-value detalle-obs">{{ detalleEstudio.observaciones }}</div>
          </div>
        </div>

        <div v-if="detalleEstudio.adjuntos?.length" class="flex flex-column gap-2">
          <div class="detalle-label">Adjuntos</div>
          <div class="flex flex-wrap gap-2">
            <a
              v-for="a in detalleEstudio.adjuntos"
              :key="a.id"
              :href="estudiosApi.getAdjuntoUrl(a.id)"
              target="_blank"
              rel="noopener"
              class="adjunto-dialog-item"
              v-tooltip="a.originalName"
            >
              <img v-if="a.mimeType?.startsWith('image/')" :src="estudiosApi.getAdjuntoUrl(a.id)" class="adjunto-dialog-thumb" alt="">
              <i v-else class="pi pi-file" />
              <span>{{ a.originalName }}</span>
            </a>
          </div>
        </div>

        <div class="flex flex-column gap-2">
          <div class="detalle-label">Notas de seguimiento</div>
          <div v-if="cargandoNotas" class="text-500 text-sm">Cargando notas...</div>
          <div v-else-if="!detalleNotas.length" class="text-500 text-sm">Sin notas cargadas todavía.</div>
          <div v-else class="flex flex-column gap-2">
            <div v-for="n in detalleNotas" :key="n.id" class="nota-item">
              <div class="nota-fecha">{{ n.fecha || formatFechaHora(n.createdAt) }}</div>
              <div class="nota-texto">{{ n.nota }}</div>
            </div>
          </div>
        </div>

        <div class="flex justify-content-end gap-2 mt-4 flex-wrap">
          <Button icon="pi pi-copy" label="Copiar mensaje" severity="success" outlined @click="abrirCopiarMensaje(detalleEstudio)" />
          <Button icon="pi pi-share-alt" label="Compartir con el asignado" severity="secondary" outlined @click="openAsignacionLinkDialog(detalleEstudio)" />
          <Button icon="pi pi-pencil" label="Editar" severity="secondary" @click="editarEstudio(detalleEstudio)" />
          <Button label="Cerrar" severity="secondary" outlined @click="showEstudioDialog = false" />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: mensaje editable con los datos del estudio para copiar y pegar en WhatsApp -->
    <Dialog
      v-model:visible="showCopiarDialog"
      header="Mensaje para WhatsApp"
      modal
      maximizable
      :breakpoints="{ '768px': '100vw' }"
      style="width: min(680px, 95vw)"
    >
      <div class="flex flex-column gap-3">
        <small class="text-500">
          Revisá y editá el texto como quieras. Los *asteriscos* se ven en negrita en WhatsApp.
        </small>
        <Textarea
          v-model="copiarTexto"
          v-focus
          rows="12"
          autoResize
          class="w-full mensaje-copiar"
        />
        <div class="flex gap-2 justify-content-between flex-wrap">
          <Button
            icon="pi pi-refresh"
            label="Restablecer"
            severity="secondary"
            text
            v-tooltip="'Volver a armar el mensaje con los datos del estudio'"
            @click="copiarTexto = armarMensajeCopia(copiarEstudio)"
          />
          <div class="flex gap-2">
            <Button label="Cerrar" severity="secondary" outlined @click="showCopiarDialog = false" />
            <Button
              :icon="copiarHecho ? 'pi pi-check' : 'pi pi-copy'"
              :label="copiarHecho ? '¡Copiado!' : 'Copiar'"
              :severity="copiarHecho ? 'success' : undefined"
              :disabled="!copiarTexto.trim()"
              @click="copiarMensaje"
            />
          </div>
        </div>
      </div>
    </Dialog>

    <!-- Dialog: grupos de invitados que aún no cargaron ningún estudio -->
    <Dialog v-model:visible="showPendientesDialog" header="Grupos sin informar estudios" modal style="width: min(520px, 95vw)">
      <div class="flex flex-column gap-3">
        <Dropdown
          v-model="filtroZonaPendientes"
          :options="zonaOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Todas las congregaciones"
          filter
          showClear
          class="w-full"
        />
        <small v-if="gruposSinInformarFiltrados.length" class="text-500">
          Estos grupos de invitados todavía no cargaron ningún estudio.
        </small>
        <small v-else class="text-500">Todos los grupos de invitados informaron al menos un estudio.</small>
        <div
          v-for="g in gruposSinInformarFiltrados"
          :key="g.id"
          class="flex align-items-center justify-content-between gap-2 pendiente-item"
        >
          <div>
            <div class="font-medium">{{ g.codigo }}<span v-if="g.encargado"> — {{ g.encargado }}</span></div>
            <div class="text-sm text-500">{{ zonaNombre(g.zonaId) || 'Sin congregación asignada' }}</div>
            <div v-if="g.cantidad" class="text-sm text-500">{{ g.cantidad }} persona{{ g.cantidad === 1 ? '' : 's' }}</div>
            <div v-if="g.fechas" class="text-sm text-500">{{ g.fechas }}</div>
          </div>
          <Button
            v-if="g.whatsapp"
            icon="pi pi-whatsapp"
            text
            rounded
            severity="success"
            v-tooltip="'Enviar WhatsApp'"
            @click="abrirEnviarWhatsappGrupo(g)"
          />
        </div>
      </div>
      <div class="flex justify-content-end mt-3">
        <Button label="Cerrar" severity="secondary" @click="showPendientesDialog = false" />
      </div>
    </Dialog>

    <!-- Dialog: enviar WhatsApp usando una plantilla de mensaje -->
    <Dialog v-model:visible="showWaDialog" header="Enviar WhatsApp" modal style="width: min(480px, 95vw)">
      <div class="flex flex-column gap-3 pt-2">
        <div v-if="plantillas.length" class="flex flex-column gap-1">
          <label class="text-sm">Plantilla</label>
          <Dropdown
            v-model="waPlantillaId"
            :options="plantillas"
            optionLabel="nombre"
            optionValue="id"
            placeholder="Sin plantilla"
            showClear
            class="w-full"
          />
        </div>
        <Textarea v-model="waMensaje" rows="6" autoResize placeholder="Escribí el mensaje..." />
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" @click="showWaDialog = false" />
          <Button icon="pi pi-whatsapp" label="Enviar por WhatsApp" @click="enviarWaCompuesto" />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: estadísticas por congregación y por grupo de invitados -->
    <Dialog v-model:visible="showStatsDialog" header="Estadísticas de estudios" modal style="width: min(720px, 95vw)">
      <div class="flex flex-column gap-4">
        <div class="flex gap-4 flex-wrap">
          <div class="stat-box">
            <div class="stat-value">{{ statsResumen.totalEstudios }}</div>
            <div class="stat-label">Estudios en total</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">{{ statsResumen.promedioPorGrupo }}</div>
            <div class="stat-label">Promedio por grupo</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">{{ statsResumen.promedioPorZona }}</div>
            <div class="stat-label">Promedio por congregación</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">{{ statsResumen.promedioPorGrupoInformo }}</div>
            <div class="stat-label">Promedio por grupo que informó</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">{{ statsResumen.totalGruposPendientes }}</div>
            <div class="stat-label">Grupos sin informar</div>
          </div>
        </div>
        <div>
          <div class="text-sm font-medium mb-2">Por congregación</div>
          <DataTable :value="statsPorZona" size="small" class="p-datatable-sm" emptyMessage="Sin datos">
            <Column field="nombre" header="Congregación" />
            <Column header="Grupos" style="width: 80px; text-align: center">
              <template #body="{ data }">{{ data.grupos }}</template>
            </Column>
            <Column header="Informaron" style="width: 100px; text-align: center">
              <template #body="{ data }">{{ data.gruposInformaron }} / {{ data.grupos }}</template>
            </Column>
            <Column field="estudios" header="Estudios" style="width: 90px; text-align: center" />
            <Column header="Promedio/grupo" style="width: 100px; text-align: center">
              <template #body="{ data }">{{ data.promedio }}</template>
            </Column>
            <Column header="Promedio/grupo (informó)" style="width: 130px; text-align: center">
              <template #body="{ data }">{{ data.promedioInformaron }}</template>
            </Column>
          </DataTable>
        </div>
        <div>
          <div class="text-sm font-medium mb-2">Por grupo de invitados</div>
          <DataTable
            :value="statsPorGrupo"
            size="small"
            class="p-datatable-sm"
            :paginator="statsPorGrupo.length > 8"
            :rows="8"
            emptyMessage="Sin datos"
          >
            <Column field="codigo" header="Grupo" />
            <Column field="zonaNombre" header="Congregación" />
            <Column field="encargado" header="Encargado" />
            <Column field="estudios" header="Estudios" style="width: 90px; text-align: center" />
            <Column header="Estado" style="width: 110px">
              <template #body="{ data }">
                <Tag :severity="data.informo ? 'success' : 'warn'" :value="data.informo ? 'Informó' : 'Pendiente'" />
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
      <div class="flex justify-content-end mt-3">
        <Button label="Cerrar" severity="secondary" @click="showStatsDialog = false" />
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
.estudios-page { width: 100%; padding: var(--space-4); }
.estudios-header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
  text-align: left;
}
.estudios-title {
  font-size: var(--text-title-2);
  font-weight: var(--weight-semibold);
  color: var(--label-primary);
}
.estudios-sub {
  font-size: var(--text-footnote);
  color: var(--label-secondary);
}
.picker-map-wrap {
  position: relative;
}
.mapa-capa-toggle {
  position: absolute;
  top: var(--space-2, 8px);
  right: var(--space-2, 8px);
  z-index: 500;
}
.picker-map {
  width: 100%;
  height: 320px;
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
}
.estudios-layout {
  display: grid;
  gap: var(--space-3);
}
.estudios-layout.con-mapa {
  grid-template-columns: minmax(0, 1fr) minmax(320px, 40%);
}
.estudios-lista {
  min-width: 0;
}
.estudios-mapa-panel {
  position: relative;
  grid-column: 2;
  grid-row: 1;
  position: sticky;
  top: var(--space-4);
  align-self: start;
  height: calc(100vh - 2 * var(--space-4, 16px));
}
@media (max-width: 768px) {
  .estudios-layout.con-mapa {
    grid-template-columns: minmax(0, 1fr);
  }
  .estudios-mapa-panel {
    grid-column: 1;
    position: static;
    height: 300px;
  }
  .estudios-lista {
    grid-row: 2;
  }
}
.estudio-numero {
  min-width: 1.75rem;
  height: 1.75rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: var(--label-primary, #111827);
  color: var(--surface-card, #fff);
  font-size: 0.8rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.mapa-filtro-asignado {
  position: absolute;
  bottom: var(--space-3, 12px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 500;
  white-space: nowrap;
}
.mapa-filtro-asignado :deep(.p-button) {
  padding: 0.35rem 0.7rem;
  font-size: 0.85rem;
}
.estudios-mapa-panel :deep(.estudio-marcador-numero) {
  background: transparent;
  border: none;
}
.estudios-mapa-panel :deep(.estudio-marcador-numero span) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.6);
}
.mapa-acciones {
  position: absolute;
  top: var(--space-2, 8px);
  right: var(--space-2, 8px);
  z-index: 500;
  display: flex;
  gap: var(--space-2, 8px);
}
.estudios-mapa-panel.mapa-pantalla-completa {
  position: fixed;
  inset: 0;
  z-index: 1000;
  height: 100vh;
  height: 100dvh;
  background: var(--surface-ground, #fff);
}
.mapa-pantalla-completa .estudios-mapa-vista {
  border-radius: 0;
}
.estudio-card-resaltada {
  box-shadow: 0 0 0 3px var(--primary-color, #3b82f6);
  transition: box-shadow 0.3s;
}
.estudios-mapa-vista {
  width: 100%;
  height: 100%;
  min-height: 300px;
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
}
.mini-map {
  width: 100%;
  height: 160px;
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
}
.mini-map-clickable {
  cursor: pointer;
}
.pendiente-item {
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--surface-border, #e5e7eb);
}
.pendiente-item:last-child {
  border-bottom: none;
}
.adjunto-dialog-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2);
  border-radius: var(--radius-md, 8px);
  background: var(--surface-100, #f4f4f5);
  color: var(--label-primary, #111);
  text-decoration: none;
}
.adjunto-dialog-thumb {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  object-fit: cover;
}
.mensaje-copiar {
  font-size: 1rem;
  line-height: 1.5;
  min-height: 16rem;
}
.stat-box {
  background: var(--surface-100, #f4f4f5);
  border-radius: var(--radius-md, 8px);
  padding: var(--space-3) var(--space-4);
  min-width: 140px;
  text-align: center;
}
.stat-value {
  font-size: var(--text-title-2);
  font-weight: var(--weight-semibold);
  color: var(--label-primary);
}
.stat-label {
  font-size: var(--text-footnote);
  color: var(--label-secondary);
}
.estudios-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(360px, 100%), 1fr));
  gap: var(--space-3);
}
.estudio-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-3);
  background: var(--surface-card, #fff);
  border: 1px solid var(--surface-border, #e5e7eb);
  border-left: 4px solid var(--urg-color);
  border-radius: var(--radius-md, 8px);
  text-align: left;
}
.card-head {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}
.card-head-btn {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
}
.card-head-editor {
  min-width: 10rem;
}
.card-campos {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-2) var(--space-3);
}
.campo {
  min-width: 0;
}
.campo-ancho {
  grid-column: 1 / -1;
}
.campo-valor {
  font-size: var(--text-body, 0.95rem);
  color: var(--label-primary);
  padding: 2px 4px;
  margin: 0 -4px;
  border-radius: 4px;
  cursor: text;
  overflow-wrap: anywhere;
  min-height: 1.5rem;
}
.campo-valor:hover,
.campo-valor:focus-visible {
  background: var(--surface-100, #f4f4f5);
  outline: none;
}
.resultado-geo {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-2);
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: var(--radius-md, 8px);
  background: transparent;
  color: var(--label-primary);
  text-align: left;
  cursor: pointer;
}
.resultado-geo-activo {
  border-color: var(--primary-color, #3b82f6);
  background: var(--highlight-bg, rgba(59, 130, 246, 0.08));
}
.card-mapa {
  width: 100%;
  height: 140px;
  margin-top: var(--space-1, 4px);
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
  cursor: pointer;
}
.card-mapa :deep(.leaflet-container) {
  cursor: pointer;
}
.campo-obs {
  white-space: pre-wrap;
  line-height: 1.4;
}
.card-foot {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  flex-wrap: wrap;
  padding-top: var(--space-2);
  border-top: 1px solid var(--surface-border, #e5e7eb);
}
.urgencia-badge-vacia {
  background: var(--surface-200, #e4e4e7);
  color: var(--label-secondary, #71717a);
}
.busqueda-estudios {
  max-width: 40rem;
}
.detalle-label {
  font-size: var(--text-footnote);
  color: var(--label-secondary);
  margin-bottom: 2px;
}
.detalle-value {
  font-size: var(--text-body, 0.95rem);
  color: var(--label-primary);
}
.detalle-obs {
  white-space: pre-wrap;
  line-height: 1.4;
}
.nota-item {
  background: var(--surface-100, #f4f4f5);
  border-radius: var(--radius-md, 8px);
  padding: var(--space-2) var(--space-3);
}
.nota-fecha {
  font-size: var(--text-footnote);
  color: var(--label-secondary);
  margin-bottom: 2px;
}
.nota-texto {
  font-size: var(--text-body, 0.95rem);
  color: var(--label-primary);
  white-space: pre-wrap;
  line-height: 1.4;
}
.urgencia-chip {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  border: 2px solid var(--chip-color);
  background: transparent;
  color: var(--label-primary);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.urgencia-chip-active {
  background: var(--chip-color);
  color: #fff;
}
.urgencia-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  color: #fff;
  font-weight: 600;
  font-size: 0.8rem;
}
</style>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick, effectScope } from 'vue'
import L from 'leaflet'
import { useTerritorioStore } from '@/store/territorio'
import { useTerritoriosStore } from '@/store/territorios'
import { useSalidaStore } from '@/store/useSalidaStore'
import { useRouterStore } from '@/store/router'
import { useEstudios } from '@/composables/useEstudios'
import { useGruposInvitados } from '@/composables/useGruposInvitados'
import { useCatalogos } from '@/composables/useCatalogos'
import { useMapTiles } from '@/composables/useMapTiles'
import { useGoogleMapsUrl } from '@/composables/useGoogleMapsUrl'
import { useNominatim } from '@/composables/useNominatim'
import Tag from 'primevue/tag'
import { useToast } from 'primevue/usetoast'
import InputSwitch from 'primevue/inputswitch'
import Paginator from 'primevue/paginator'
import {
  useMensajePlantilla,
  DEFAULT_MENSAJE_PLANTILLA,
  nuevaPlantilla,
  renderMensajePlantilla,
} from '@/composables/useMensajePlantilla'

const terr = useTerritorioStore()
const territorios = useTerritoriosStore()
const salidaStore = useSalidaStore()
const route = useRouterStore()
const toast = useToast()

// Escala de urgencia 1-5 (verde → rojo) usada en el formulario, la tabla y el filtro
const URGENCIA_NIVELES = [
  { value: 1, label: 'Baja', color: '#22c55e' },
  { value: 2, label: 'Media-baja', color: '#84cc16' },
  { value: 3, label: 'Media', color: '#eab308' },
  { value: 4, label: 'Alta', color: '#f97316' },
  { value: 5, label: 'Urgente', color: '#ef4444' },
]
const urgenciaInfo = (nivel) => URGENCIA_NIVELES.find((u) => u.value === nivel) || null
// Mismas opciones que URGENCIA_NIVELES más "Sin urgencia" para el filtro (0 no es un nivel válido)
const URGENCIA_NIVELES_FILTRO = [...URGENCIA_NIVELES, { value: 'sin', label: 'Sin urgencia' }]
const URGENCIA_OPCIONES_EDICION = URGENCIA_NIVELES.map((n) => ({ value: n.value, label: `${n.value} · ${n.label}` }))

const TIPO_OPCIONES = [
  { value: 'visita_inicial', label: 'Visita inicial', severity: 'secondary' },
  { value: 'revisita', label: 'Revisita', severity: 'warn' },
  { value: 'estudio_iniciado', label: 'Estudio iniciado', severity: 'info' },
  { value: 'estudio_establecido', label: 'Estudio establecido', severity: 'success' },
]
const tipoInfo = (tipo) => TIPO_OPCIONES.find((t) => t.value === tipo) || TIPO_OPCIONES[2]
const SI_NO_OPCIONES = [
  { value: 'si', label: 'Sí' },
  { value: 'no', label: 'No' },
]
// Filtro rápido sobre el mapa; comparte estado con el filtro "Asignado" de arriba
const FILTRO_ASIGNADO_MAPA = [
  { value: null, label: 'Todos' },
  { value: 'si', label: 'Asignados' },
  { value: 'no', label: 'Sin asignar' },
]

const escapeHtml = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[c]))

const estudiosApi = useEstudios()
const gruposInvitadosApi = useGruposInvitados()
const catalogosApi = useCatalogos()
const mensajeApi = useMensajePlantilla()

const formVacio = () => ({
  territorioId: terr.uuid || null,
  guestGroupId: null,
  publicador: '',
  whatsapp: '',
  estudiante: '',
  estudianteWhatsapp: '',
  direccion: '',
  lat: null,
  lng: null,
  fechaInicio: '',
  aQuienSePaso: '',
  observaciones: '',
  urgencia: null,
  tipo: 'estudio_iniciado',
  asignadoA: '',
  asignadoAWhatsapp: '',
  asignado: false,
  atendiendose: false,
})
const form = ref(formVacio())
const editandoId = ref(null)
const filtroTerritorioId = ref(null)
const filtroGrupoId = ref(null)
const filtroZonaId = ref(null)
const filtroUrgencia = ref([])
const filtroTipo = ref(null)
const filtroAsignado = ref(null)
const filtroAtendiendose = ref(null)
const busqueda = ref('')
const guardando = ref(false)
const cargando = ref(false)
const errorGuardar = ref(null)

const FILAS_PAGINA_KEY = 'estudios:tarjetasPorPagina'
const filasPorPagina = ref(Number(localStorage.getItem(FILAS_PAGINA_KEY)) || 12)
const primerIndice = ref(0)
const FILAS_PAGINA_OPCIONES = [6, 12, 24, 48, 96]
const cambiarFilasPorPagina = (n) => {
  if (!n || n === filasPorPagina.value) return
  filasPorPagina.value = n
  primerIndice.value = 0
  localStorage.setItem(FILAS_PAGINA_KEY, n)
}
const onPageChange = (e) => {
  primerIndice.value = e.first
  if (e.rows === filasPorPagina.value) return
  filasPorPagina.value = e.rows
  localStorage.setItem(FILAS_PAGINA_KEY, e.rows)
}

const estudios = ref([])

const loadEstudios = async () => {
  cargando.value = true
  try {
    const result = await estudiosApi.getEstudios()
    estudios.value = result.estudios || []
  } finally {
    cargando.value = false
  }
}

// Grupos de invitados para asociar estudios y compartir enlaces
const guestGroups = ref([])
const loadGuestGroups = async () => {
  const result = await gruposInvitadosApi.getGruposInvitados()
  guestGroups.value = result.guestGroups || []
}
const guestGroupOptions = computed(() =>
  guestGroups.value.map((g) => ({ id: g.id, label: g.encargado ? `${g.codigo} - ${g.encargado}` : g.codigo }))
)
const guestGroupLabel = (guestGroupId) => guestGroupOptions.value.find((g) => g.id === guestGroupId)?.label || ''

// Zonas (= congregaciones de los grupos de invitados, mismo catálogo que en Parámetros/AdminPanel)
const zonas = ref([])
const loadZonas = async () => {
  const result = await catalogosApi.getZonas()
  zonas.value = result.zonas || []
}
const zonaOptions = computed(() => zonas.value.map((z) => ({ label: z.name, value: z.id })))
const zonaNombre = (zonaId) => zonas.value.find((z) => z.id === zonaId)?.name || ''
const zonaIdDeGrupo = (guestGroupId) => guestGroups.value.find((g) => g.id === guestGroupId)?.zonaId || null

// Opciones de territorio para los selectores
const territorioOptions = computed(() =>
  territorios.listWithName.map(t => ({ label: t.territorio_nombre, value: t.uuid }))
)
const territorioNombre = (uuid) => {
  const t = territorios.list.find(t => t.uuid === uuid)
  return t ? `${t.zona || ''}${t.numero}` : ''
}

// ─── Búsqueda de texto libre ─────────────────────────────────────────────────
// Sin distinguir mayúsculas ni acentos; cada palabra buscada tiene que aparecer
// en algún dato del estudio (AND). Los teléfonos también se comparan sólo por
// dígitos, así "2645555" encuentra "+54 9 264 555 5555".
const normalizar = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
const soloDigitos = (s) => String(s ?? '').replace(/\D/g, '')

const textoBuscable = (e) => {
  const grupo = guestGroups.value.find((g) => g.id === e.guestGroupId)
  const partes = [
    e.estudiante, e.estudianteWhatsapp, e.publicador, e.whatsapp, e.asignadoA, e.asignadoAWhatsapp,
    e.direccion, e.fechaInicio, e.aQuienSePaso, e.observaciones, e.territorioNombre,
    grupo?.codigo, grupo?.encargado, zonaNombre(grupo?.zonaId),
    tipoInfo(e.tipo).label, urgenciaInfo(e.urgencia)?.label,
    e.asignado ? 'asignado' : '', e.atendiendose ? 'atendiendose' : '',
    ...(e.adjuntos || []).map((a) => a.originalName),
  ]
  return {
    texto: normalizar(partes.filter(Boolean).join(' ')),
    digitos: [e.estudianteWhatsapp, e.whatsapp, e.asignadoAWhatsapp].map(soloDigitos).join(' '),
  }
}

const terminosBusqueda = computed(() => normalizar(busqueda.value).split(/\s+/).filter(Boolean))

const coincideBusqueda = (e) => {
  if (!terminosBusqueda.value.length) return true
  const { texto, digitos } = textoBuscable(e)
  return terminosBusqueda.value.every((t) => {
    if (texto.includes(t)) return true
    const d = soloDigitos(t)
    return d.length >= 3 && /^[\d+\-()]+$/.test(t) && digitos.includes(d)
  })
}

const estudiosFiltrados = computed(() =>
  estudios.value
    .filter(e => !filtroTerritorioId.value || e.territorioId === filtroTerritorioId.value)
    .filter(e => !filtroGrupoId.value || e.guestGroupId === filtroGrupoId.value)
    .filter(e => !filtroZonaId.value || zonaIdDeGrupo(e.guestGroupId) === filtroZonaId.value)
    .filter(e => !filtroUrgencia.value?.length || filtroUrgencia.value.includes(e.urgencia ? e.urgencia : 'sin'))
    .filter(e => !filtroTipo.value || e.tipo === filtroTipo.value)
    .filter(e => !filtroAsignado.value || (filtroAsignado.value === 'si' ? !!e.asignado : !e.asignado))
    .filter(e => !filtroAtendiendose.value || (filtroAtendiendose.value === 'si' ? !!e.atendiendose : !e.atendiendose))
    .map(e => ({ ...e, territorioNombre: territorioNombre(e.territorioId) }))
    .filter(coincideBusqueda)
)

const estudiosPagina = computed(() =>
  estudiosFiltrados.value.slice(primerIndice.value, primerIndice.value + filasPorPagina.value)
)

// Al buscar o filtrar, volver a la primera página
watch(() => estudiosFiltrados.value.length, () => { primerIndice.value = 0 })

// Grupos de invitados que todavía no cargaron ningún estudio
const gruposSinInformar = computed(() =>
  guestGroups.value.filter((g) => !estudios.value.some((e) => e.guestGroupId === g.id))
)
const filtroZonaPendientes = ref(null)
const gruposSinInformarFiltrados = computed(() =>
  gruposSinInformar.value.filter(
    (g) => !filtroZonaPendientes.value || g.zonaId === filtroZonaPendientes.value
  )
)

// Estadísticas por grupo de invitados: cantidad de estudios y si ya informó
const statsPorGrupo = computed(() => {
  const rows = guestGroups.value.map((g) => {
    const count = estudios.value.filter((e) => e.guestGroupId === g.id).length
    return {
      id: g.id,
      codigo: g.codigo,
      encargado: g.encargado || '-',
      zonaNombre: zonaNombre(g.zonaId) || 'Sin congregación',
      estudios: count,
      informo: count > 0,
    }
  })
  const sinGrupo = estudios.value.filter((e) => !e.guestGroupId).length
  if (sinGrupo) {
    rows.push({ id: '__sin_grupo__', codigo: 'Sin grupo asignado', encargado: '-', zonaNombre: '-', estudios: sinGrupo, informo: true })
  }
  return rows.sort((a, b) => a.codigo.localeCompare(b.codigo))
})

const promedio = (total, cantidad) => (cantidad > 0 ? (total / cantidad).toFixed(1) : '0.0')

// Estadísticas por congregación, agregando statsPorGrupo (excluye la fila "sin grupo")
const statsPorZona = computed(() => {
  const map = new Map()
  statsPorGrupo.value
    .filter((row) => row.id !== '__sin_grupo__')
    .forEach((row) => {
      if (!map.has(row.zonaNombre)) {
        map.set(row.zonaNombre, { nombre: row.zonaNombre, grupos: 0, gruposInformaron: 0, estudios: 0 })
      }
      const entry = map.get(row.zonaNombre)
      entry.grupos++
      if (row.informo) entry.gruposInformaron++
      entry.estudios += row.estudios
    })
  return [...map.values()]
    .map((entry) => ({
      ...entry,
      promedio: promedio(entry.estudios, entry.grupos),
      promedioInformaron: promedio(entry.estudios, entry.gruposInformaron),
    }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre))
})

// Resumen general: totales y promedios (por grupo de invitados, por congregación y por grupo que informó)
const statsResumen = computed(() => {
  const totalEstudios = estudios.value.length
  const totalGrupos = guestGroups.value.length
  const totalZonas = statsPorZona.value.length
  const totalGruposInformaron = statsPorGrupo.value.filter((row) => row.id !== '__sin_grupo__' && row.informo).length
  return {
    totalEstudios,
    promedioPorGrupo: promedio(totalEstudios, totalGrupos),
    promedioPorZona: promedio(totalEstudios, totalZonas),
    promedioPorGrupoInformo: promedio(totalEstudios, totalGruposInformaron),
    totalGruposPendientes: totalGrupos - totalGruposInformaron,
  }
})

const guardarEstudio = async () => {
  guardando.value = true
  errorGuardar.value = null
  try {
    const result = editandoId.value
      ? await estudiosApi.updateEstudio(editandoId.value, form.value)
      : await estudiosApi.createEstudio(form.value)
    if (result.error) {
      if (result.code === 'STALE') {
        errorGuardar.value = 'Otro usuario modificó este estudio. Se recargó la lista, revisá y volvé a guardar.'
        await loadEstudios()
        return
      }
      errorGuardar.value = result.error
      return
    }
    cerrarEstudioDialog()
    await loadEstudios()
  } finally {
    guardando.value = false
  }
}

// ─── Edición directa en la lista ─────────────────────────────────────────────
// Los guardados se encadenan para que dos cambios seguidos sobre la misma fila
// usen el updatedAt que devolvió el anterior y no choquen con el control de concurrencia.
let colaGuardado = Promise.resolve()

const guardarCampos = (id, cambios) => {
  colaGuardado = colaGuardado.then(async () => {
    const original = estudios.value.find((e) => e.id === id)
    if (!original) return
    const result = await estudiosApi.updateEstudio(id, { ...camposEditables(original), ...cambios })
    if (result.error) {
      const stale = result.code === 'STALE'
      toast.add({
        severity: 'error',
        summary: 'No se pudo guardar',
        detail: stale ? 'Otro usuario modificó este estudio. Se recargó la lista, revisá y volvé a editar.' : result.error,
        life: 5000,
      })
      if (stale) await loadEstudios()
      return
    }
    Object.assign(original, cambios, { updatedAt: result.updatedAt })
    toast.add({ severity: 'success', summary: 'Guardado', life: 1500 })
  }).catch(() => {
    toast.add({ severity: 'error', summary: 'No se pudo guardar', detail: 'Error de conexión', life: 5000 })
  })
  return colaGuardado
}

// Datos de la tarjeta. `tipo`: texto (por defecto), date, multilinea o select.
const CAMPOS_CABECERA = [
  { field: 'urgencia', label: 'Urgencia', tipo: 'select', opciones: 'urgencia', placeholder: 'Sin urgencia', showClear: true },
  { field: 'tipo', label: 'Tipo', tipo: 'select', opciones: 'tipo' },
]
const CAMPOS_TARJETA = [
  { field: 'estudiante', label: 'Estudiante', destacado: true },
  { field: 'estudianteWhatsapp', label: 'Tel. estudiante', telefono: true },
  { field: 'publicador', label: 'Publicador' },
  { field: 'whatsapp', label: 'Tel. publicador', telefono: true },
  { field: 'asignadoA', label: 'Asignado a' },
  { field: 'asignadoAWhatsapp', label: 'Tel. asignado', telefono: true },
  { field: 'territorioId', label: 'Territorio', tipo: 'select', opciones: 'territorio', placeholder: 'Sin territorio' },
  { field: 'fechaInicio', label: 'Fecha de inicio', tipo: 'date' },
  { field: 'guestGroupId', label: 'Grupo', tipo: 'select', opciones: 'grupo', optionValue: 'id', placeholder: 'Sin grupo asignado', ancho: true },
  { field: 'direccion', label: 'Dirección', ancho: true },
  { field: 'aQuienSePaso', label: 'Se lo pasó a', ancho: true },
  { field: 'observaciones', label: 'Observaciones', tipo: 'multilinea', ancho: true },
]

const opcionesCampo = (c) => ({
  urgencia: URGENCIA_OPCIONES_EDICION,
  tipo: TIPO_OPCIONES,
  territorio: territorioOptions.value,
  grupo: guestGroupOptions.value,
}[c.opciones] || [])

const textoCampo = (e, c) => {
  if (c.field === 'territorioId') return e.territorioNombre
  if (c.field === 'guestGroupId') return guestGroupLabel(e.guestGroupId)
  return e[c.field]
}

// Edición de un dato por vez: { id, field, valor }
const edicion = ref(null)
const estaEditando = (e, field) => edicion.value?.id === e.id && edicion.value?.field === field

// El desplegable se abre solo al empezar a editar un dato de selección
let dropdownEditor = null
const setDropdownEditor = (el) => { dropdownEditor = el }

const empezarEdicion = async (e, field) => {
  if (estaEditando(e, field)) return
  const campo = [...CAMPOS_CABECERA, ...CAMPOS_TARJETA].find((c) => c.field === field)
  const esSelect = campo?.tipo === 'select'
  edicion.value = { id: e.id, field, valor: e[field] ?? (esSelect ? null : '') }
  if (esSelect) {
    await nextTick()
    dropdownEditor?.show?.()
  }
}

const descartarEdicion = () => { edicion.value = null }

// Si el desplegable se cierra sin elegir nada, se deja de editar ese dato
const descartarEdicionSelect = (field) => {
  if (edicion.value?.field === field) descartarEdicion()
}

const confirmarEdicion = () => {
  const actual = edicion.value
  if (!actual) return
  edicion.value = null
  const original = estudios.value.find((e) => e.id === actual.id)
  if (!original) return
  const normalizarValor = (v) => (typeof v === 'string' ? v.trim() : v ?? null) || null
  if (normalizarValor(actual.valor) === normalizarValor(original[actual.field])) return
  const valor = typeof actual.valor === 'string' ? actual.valor.trim() : actual.valor ?? null
  return guardarCampos(actual.id, { [actual.field]: valor })
}

// Enfoca el input del dato apenas aparece
const vFocus = { mounted: (el) => el.focus() }

// Todos los mapas de esta vista arrancan en el mapa plano; el satélite es
// opcional (botón sobre el mapa) y no se guarda como preferencia global.
const sateliteEstudios = ref(false)
// Mapa "limpio": sólo calles, sin negocios ni puntos de interés (se recuerda)
const MAPA_LIMPIO_KEY = 'estudios:mapaLimpio'
const mapaLimpio = ref(localStorage.getItem(MAPA_LIMPIO_KEY) === 'true')
watch(mapaLimpio, (v) => localStorage.setItem(MAPA_LIMPIO_KEY, v ? 'true' : 'false'))

// Mapita estático de la ubicación en cada tarjeta. Se crea recién cuando la
// tarjeta entra en pantalla (puede haber muchas por página) y cada uno tiene
// su propia capa de tiles, en un effectScope que se libera al desmontarse.
const crearMiniMapa = (el, coords) => {
  const state = el._miniMapa
  state.map = L.map(el, {
    dragging: false,
    zoomControl: false,
    scrollWheelZoom: false,
    doubleClickZoom: false,
    boxZoom: false,
    keyboard: false,
    touchZoom: false,
    attributionControl: false,
  }).setView(coords, 16)
  state.scope = effectScope()
  state.scope.run(() => useMapTiles(() => state.map, { satellite: sateliteEstudios, clean: mapaLimpio }).applyCurrentTiles(state.map))
  state.marker = L.circleMarker(coords, { radius: 7, weight: 3, fillOpacity: 0.6 }).addTo(state.map)
  setTimeout(() => state.map?.invalidateSize(), 100)
}

const vMiniMapa = {
  mounted(el, { value }) {
    el._miniMapa = { coords: value }
    el._miniMapa.observer = new IntersectionObserver((entries) => {
      if (!entries.some((en) => en.isIntersecting)) return
      el._miniMapa.observer.disconnect()
      crearMiniMapa(el, el._miniMapa.coords)
    }, { rootMargin: '200px' })
    el._miniMapa.observer.observe(el)
  },
  updated(el, { value, oldValue }) {
    const state = el._miniMapa
    if (!state || (value[0] === oldValue?.[0] && value[1] === oldValue?.[1])) return
    state.coords = value
    state.map?.setView(value, 16)
    state.marker?.setLatLng(value)
  },
  unmounted(el) {
    const state = el._miniMapa
    if (!state) return
    state.observer?.disconnect()
    state.scope?.stop()
    state.map?.remove()
    el._miniMapa = null
  },
}

const abrirNuevoEstudio = () => {
  editandoId.value = null
  errorGuardar.value = null
  form.value = formVacio()
  detalleEstudio.value = null
  modoEdicion.value = true
  showEstudioDialog.value = true
}

// Campos que acepta el PUT del estudio (mismos que el formulario de edición)
const camposEditables = (estudio) => ({
    territorioId: estudio.territorioId,
    guestGroupId: estudio.guestGroupId,
    publicador: estudio.publicador,
    whatsapp: estudio.whatsapp,
    estudiante: estudio.estudiante,
    estudianteWhatsapp: estudio.estudianteWhatsapp,
    direccion: estudio.direccion,
    lat: estudio.lat,
    lng: estudio.lng,
    fechaInicio: estudio.fechaInicio,
    aQuienSePaso: estudio.aQuienSePaso,
    observaciones: estudio.observaciones,
    urgencia: estudio.urgencia,
    tipo: estudio.tipo,
    asignadoA: estudio.asignadoA,
    asignadoAWhatsapp: estudio.asignadoAWhatsapp,
    asignado: estudio.asignado,
    atendiendose: estudio.atendiendose,
    updatedAt: estudio.updatedAt, // para concurrencia optimista al guardar
})

const editarEstudio = (estudio) => {
  editandoId.value = estudio.id
  detalleEstudio.value = estudio
  errorGuardar.value = null
  form.value = camposEditables(estudio)
  modoEdicion.value = true
  showEstudioDialog.value = true
  loadDetalleNotas(estudio.id)
}

// Si venía de ver el detalle de un estudio existente, cancelar vuelve a la vista
// (sin guardar los cambios); si era un estudio nuevo, cierra el modal directamente.
const cancelarEdicion = () => {
  errorGuardar.value = null
  if (editandoId.value) {
    modoEdicion.value = false
  } else {
    cerrarEstudioDialog()
  }
}

const cerrarEstudioDialog = () => {
  showEstudioDialog.value = false
  modoEdicion.value = false
  editandoId.value = null
  detalleEstudio.value = null
  form.value = formVacio()
}

// ─── Selector de ubicación en el mapa ────────────────────────────────────────
const showMapDialog = ref(false)
const mapDiv = ref(null)
const pickerLatLng = ref(null)
let pickerMap = null
let pickerMarker = null
const tiles = useMapTiles(() => pickerMap, { satellite: sateliteEstudios, clean: mapaLimpio })
const { resolveLocation } = useGoogleMapsUrl()

const pickerLinkTexto = ref('')
const pickerLinkError = ref(null)
const pickerLinkResolving = ref(false)

// El selector edita la ubicación del formulario o, si se abrió desde una
// tarjeta, la del estudio de esa tarjeta (y se guarda al confirmar).
const pickerEstudio = ref(null)
const pickerOrigen = computed(() => pickerEstudio.value || form.value)

const abrirMapaEstudio = (estudio) => {
  pickerEstudio.value = estudio
  pickerLinkTexto.value = ''
  pickerLinkError.value = null
  prepararBusquedaPicker(estudio)
  showMapDialog.value = true
}

const openMapPicker = () => {
  pickerEstudio.value = null
  prepararBusquedaPicker(form.value)
  pickerLinkTexto.value = ''
  pickerLinkError.value = null
  showMapDialog.value = true
}

// ─── Búsqueda de la dirección (geocodificación con Google en el servidor) ───
// Los estudios son de Formosa capital: las direcciones suelen cargarse sin
// ciudad, así que se completa la búsqueda con la ciudad y se limita a Argentina.
const ZONA_ESTUDIOS = {
  centro: [-26.1849, -58.1731],
  ciudad: 'Formosa, Argentina',
  pais: 'ar',
}
const conCiudad = (q) => (/formosa/i.test(q) ? q : `${q}, ${ZONA_ESTUDIOS.ciudad}`)

const { geocodeAddress } = useNominatim()
const pickerBusqueda = ref('')
const pickerBuscando = ref(false)
const pickerBusquedaError = ref(null)
const pickerResultados = ref([])
const pickerResultadoSel = ref(null)
// Si el estudio tiene dirección pero no ubicación, se busca solo al abrir el mapa
let pickerAutoBuscar = false

const prepararBusquedaPicker = (origen) => {
  pickerBusqueda.value = origen?.direccion || ''
  pickerBusquedaError.value = null
  pickerResultados.value = []
  pickerResultadoSel.value = null
  pickerAutoBuscar = !!origen?.direccion && (origen.lat == null || origen.lng == null)
}

const elegirResultadoPicker = (r) => {
  pickerResultadoSel.value = r
  pickerLatLng.value = { lat: r.lat, lng: r.lng }
  placeMarker(pickerLatLng.value)
  pickerMap?.setView([r.lat, r.lng], 17)
}

const buscarDireccionPicker = async () => {
  const q = pickerBusqueda.value.trim()
  if (!q) return
  pickerBuscando.value = true
  pickerBusquedaError.value = null
  pickerResultados.value = []
  pickerResultadoSel.value = null
  try {
    // Priorizar resultados cerca de los territorios del grupo (o del centro de Formosa)
    const [lat, lng] = grupoMapCenter() || ZONA_ESTUDIOS.centro
    const res = await geocodeAddress(conCiudad(q), { lat, lng }, ZONA_ESTUDIOS.pais)
    const resultados = (Array.isArray(res) ? res : [])
      .map((r) => ({ lat: Number(r.lat), lng: Number(r.lon), label: r.display_name }))
      .filter((r) => Number.isFinite(r.lat) && Number.isFinite(r.lng))
      .slice(0, 5)
    if (!resultados.length) {
      pickerBusquedaError.value = Array.isArray(res)
        ? 'No encontramos esa dirección. Probá agregando la ciudad o marcala tocando el mapa.'
        : 'El servicio de búsqueda no está disponible en este momento.'
      return
    }
    pickerResultados.value = resultados
    elegirResultadoPicker(resultados[0])
  } catch {
    pickerBusquedaError.value = 'No se pudo buscar la dirección.'
  } finally {
    pickerBuscando.value = false
  }
}

const usarLinkPicker = async () => {
  const texto = pickerLinkTexto.value.trim()
  if (!texto) return
  pickerLinkError.value = null
  pickerLinkResolving.value = true
  try {
    const loc = await resolveLocation(texto)
    if (!loc) {
      pickerLinkError.value = 'No pudimos reconocer una ubicación en ese texto.'
      return
    }
    pickerLatLng.value = loc
    placeMarker(loc)
    pickerMap?.setView([loc.lat, loc.lng], 17)
  } finally {
    pickerLinkResolving.value = false
  }
}

// El centro del territorio se guarda con formato variable ([lat, lng] o {lat, lng})
const normalizeCenter = (c) => {
  if (!c) return null
  if (Array.isArray(c) && Number.isFinite(Number(c[0])) && Number.isFinite(Number(c[1]))) {
    return [Number(c[0]), Number(c[1])]
  }
  if (Number.isFinite(Number(c.lat)) && Number.isFinite(Number(c.lng))) {
    return [Number(c.lat), Number(c.lng)]
  }
  return null
}

// Centro de alguno de los territorios asignados al grupo de invitados del formulario,
// para abrir el mapa ahí y que sea más fácil guiarse. La relación grupo→territorios
// es la misma que usa AdminPanel: salidas que comparten algún grupo de predicación.
const grupoMapCenter = () => {
  const grupo = guestGroups.value.find(g => g.id === pickerOrigen.value.guestGroupId)
  if (!grupo?.grupos?.length) return null
  const keys = new Set()
  ;(salidaStore.list.data || [])
    .filter(s => (s.grupos || []).some(gid => grupo.grupos.includes(gid)))
    .forEach(s => (s.territorios || []).forEach(t => keys.add(String(t).toUpperCase())))
  if (!keys.size) return null
  for (const t of territorios.list) {
    if (!keys.has(`${t.zona || ''}${t.numero}`.toUpperCase())) continue
    const center = normalizeCenter(t.center)
    if (center) return center
  }
  return null
}

const placeMarker = (latlng) => {
  if (pickerMarker) {
    pickerMarker.setLatLng(latlng)
  } else {
    pickerMarker = L.circleMarker(latlng, { radius: 9, weight: 3, fillOpacity: 0.6 }).addTo(pickerMap)
  }
}

const initPickerMap = async () => {
  await nextTick()
  const origen = pickerOrigen.value
  const hasCoords = origen.lat != null && origen.lng != null
  const grupoCenter = hasCoords ? null : grupoMapCenter()
  const start = hasCoords ? [origen.lat, origen.lng] : (grupoCenter || ZONA_ESTUDIOS.centro)
  pickerMap = L.map(mapDiv.value).setView(start, hasCoords ? 17 : (grupoCenter ? 16 : 14))
  tiles.applyCurrentTiles(pickerMap)
  pickerLatLng.value = hasCoords ? { lat: origen.lat, lng: origen.lng } : null
  if (pickerLatLng.value) placeMarker(pickerLatLng.value)
  pickerMap.on('click', (e) => {
    pickerLatLng.value = e.latlng
    placeMarker(e.latlng)
  })
  setTimeout(() => pickerMap?.invalidateSize(), 100)
  if (pickerAutoBuscar) {
    pickerAutoBuscar = false
    buscarDireccionPicker()
  }
}

const destroyPickerMap = () => {
  pickerMap?.remove()
  pickerMap = null
  pickerMarker = null
  pickerLatLng.value = null
}

const aplicarMarca = (lat, lng) => {
  if (pickerEstudio.value) {
    guardarCampos(pickerEstudio.value.id, { lat, lng })
  } else {
    form.value.lat = lat
    form.value.lng = lng
  }
  showMapDialog.value = false
}

const confirmarMarca = () => aplicarMarca(pickerLatLng.value?.lat ?? null, pickerLatLng.value?.lng ?? null)

const quitarMarca = () => aplicarMarca(null, null)

// ─── Mapa con todos los estudios que tienen ubicación marcada ───────────────
const VISTA_MAPA_KEY = 'estudios:mostrarMapa'
const vistaMapa = ref(localStorage.getItem(VISTA_MAPA_KEY) !== 'false')
const estudiosMapDiv = ref(null)
let estudiosMap = null
let estudiosMarkersGroup = null
const estudiosMapTiles = useMapTiles(() => estudiosMap, { satellite: sateliteEstudios, clean: mapaLimpio })

// Mismos estudios que la tabla (respeta todos los filtros activos), sólo que acá
// nos quedamos únicamente con los que tienen una ubicación marcada en el mapa.
const estudiosConUbicacion = computed(() =>
  estudiosFiltrados.value.filter((e) => e.lat != null && e.lng != null)
)

// Número fijo de cada estudio (lo asigna el servidor al crearlo): es el mismo en
// la tarjeta y en el punto del mapa, y no cambia con los filtros.
const numeroEstudio = computed(() => new Map(estudios.value.map((e) => [e.id, e.numero ?? '–'])))

const marcadorTooltip = (estudio) => {
  const titulo = estudio.estudiante || estudio.publicador || 'Estudio'
  const partes = [`<b>${escapeHtml(titulo)}</b>`]
  if (estudio.direccion) partes.push(escapeHtml(estudio.direccion))
  if (estudio.territorioNombre) partes.push(escapeHtml(estudio.territorioNombre))
  return partes.join('<br>')
}

const drawEstudiosMarkers = () => {
  if (!estudiosMap) return
  if (estudiosMarkersGroup) estudiosMarkersGroup.remove()
  const markers = estudiosConUbicacion.value.map((estudio) => {
    const color = urgenciaInfo(estudio.urgencia)?.color || '#3b82f6'
    const marker = L.marker([estudio.lat, estudio.lng], {
      icon: L.divIcon({
        className: 'estudio-marcador-numero',
        html: `<span style="background:${color}">${numeroEstudio.value.get(estudio.id)}</span>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
      }),
    })
    marker.bindTooltip(marcadorTooltip(estudio), { sticky: true })
    marker.on('click', () => {
      // En pantalla completa las tarjetas quedan tapadas: salir para mostrar la tarjeta
      mapaPantallaCompleta.value = false
      enfocarTarjeta(estudio)
    })
    return marker
  })
  estudiosMarkersGroup = L.layerGroup(markers).addTo(estudiosMap)
  // Reencuadrar sólo si cambió qué estudios se muestran (no al editar un dato de una tarjeta)
  const clave = estudiosConUbicacion.value.map((e) => `${e.id}:${e.lat},${e.lng}`).join('|')
  if (clave === ultimoEncuadre) return
  ultimoEncuadre = clave
  if (markers.length) {
    estudiosMap.fitBounds(markers.map((m) => m.getLatLng()), { padding: [30, 30], maxZoom: 16 })
  }
}

let ultimoEncuadre = null

// Tocar un punto del mapa lleva a su tarjeta (cambiando de página si hace falta) y la resalta
const resaltadoId = ref(null)
let resaltadoTimer = null
const enfocarTarjeta = async (estudio) => {
  const idx = estudiosFiltrados.value.findIndex((e) => e.id === estudio.id)
  if (idx < 0) return
  primerIndice.value = Math.floor(idx / filasPorPagina.value) * filasPorPagina.value
  await nextTick()
  document.getElementById(`estudio-${estudio.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  resaltadoId.value = estudio.id
  clearTimeout(resaltadoTimer)
  resaltadoTimer = setTimeout(() => { resaltadoId.value = null }, 2000)
}

const destroyEstudiosMap = () => {
  estudiosMap?.remove()
  estudiosMap = null
  estudiosMarkersGroup = null
  ultimoEncuadre = null
}

// El mapa general se muestra junto a las tarjetas; se arma cuando su div está
// en el DOM y se destruye al ocultarlo o salir de la página.
const initEstudiosMap = async () => {
  destroyEstudiosMap()
  if (!vistaMapa.value) return
  await nextTick()
  if (!estudiosMapDiv.value) return
  estudiosMap = L.map(estudiosMapDiv.value).setView(ZONA_ESTUDIOS.centro, 13)
  estudiosMapTiles.applyCurrentTiles(estudiosMap)
  drawEstudiosMarkers()
  setTimeout(() => estudiosMap?.invalidateSize(), 100)
}

watch(vistaMapa, (show) => {
  localStorage.setItem(VISTA_MAPA_KEY, show ? 'true' : 'false')
  if (!show) mapaPantallaCompleta.value = false
  initEstudiosMap()
})

// Pantalla completa del mapa general: el panel pasa a cubrir toda la ventana
const mapaPantallaCompleta = ref(false)
const onMapaKeydown = (ev) => {
  if (ev.key === 'Escape') mapaPantallaCompleta.value = false
}
watch(mapaPantallaCompleta, async (full) => {
  if (full) window.addEventListener('keydown', onMapaKeydown)
  else window.removeEventListener('keydown', onMapaKeydown)
  await nextTick()
  estudiosMap?.invalidateSize()
})
onUnmounted(() => window.removeEventListener('keydown', onMapaKeydown))
onMounted(initEstudiosMap)

// Si cambian los filtros con el mapa abierto, recalcular los puntos visibles
watch(estudiosConUbicacion, () => { if (estudiosMap) drawEstudiosMarkers() })

onUnmounted(destroyEstudiosMap)

// ─── Grupos sin informar y estadísticas ──────────────────────────────────────
const showPendientesDialog = ref(false)
const showStatsDialog = ref(false)

// ─── Detalle / edición de un estudio (un único modal con vista y edición) ────
const showEstudioDialog = ref(false)
const modoEdicion = ref(false)
const detalleEstudio = ref(null)
const detalleNotas = ref([])
const cargandoNotas = ref(false)
const loadDetalleNotas = async (estudioId) => {
  cargandoNotas.value = true
  try {
    const result = await estudiosApi.getEstudioNotas(estudioId)
    detalleNotas.value = result.notas || []
  } finally {
    cargandoNotas.value = false
  }
}
const formatFechaHora = (iso) => {
  if (!iso) return ''
  const d = new Date(iso.replace(' ', 'T') + 'Z')
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}
const verDetalle = (estudio) => {
  detalleEstudio.value = estudio
  editandoId.value = null
  errorGuardar.value = null
  modoEdicion.value = false
  showEstudioDialog.value = true
  loadDetalleNotas(estudio.id)
}
const estudioDialogHeader = computed(() => {
  if (!modoEdicion.value) return 'Detalle del estudio'
  return editandoId.value ? 'Editar estudio' : 'Nuevo estudio'
})

// ─── Mapita de solo lectura dentro del modal de detalle/edición ─────────────
const miniMapDiv = ref(null)
let miniMap = null
const miniMapTiles = useMapTiles(() => miniMap, { satellite: sateliteEstudios, clean: mapaLimpio })

const miniMapCoords = computed(() => {
  if (!showEstudioDialog.value) return null
  const source = modoEdicion.value ? form.value : detalleEstudio.value
  return source?.lat != null && source?.lng != null ? [source.lat, source.lng] : null
})

const destroyMiniMap = () => {
  miniMap?.remove()
  miniMap = null
}

watch(miniMapCoords, async (coords) => {
  destroyMiniMap()
  if (!coords) return
  await nextTick()
  if (!miniMapDiv.value) return
  miniMap = L.map(miniMapDiv.value, {
    dragging: false,
    zoomControl: false,
    scrollWheelZoom: false,
    doubleClickZoom: false,
    boxZoom: false,
    keyboard: false,
    touchZoom: false,
    attributionControl: false,
  }).setView(coords, 16)
  miniMapTiles.applyCurrentTiles(miniMap)
  L.circleMarker(coords, { radius: 8, weight: 3, fillOpacity: 0.6 }).addTo(miniMap)
  setTimeout(() => miniMap?.invalidateSize(), 100)
})

onUnmounted(destroyMiniMap)

// ─── Enlace público por grupo + envío por WhatsApp con plantilla ─────────────
const showLinkDialog = ref(false)
const linkGroupId = ref(null)
const linkUrl = ref('')
const linkLoading = ref(false)
const linkCopied = ref(false)
const plantillas = ref([])
const selectedPlantillaId = ref(null)
const messageDraft = ref('')

const linkGroup = computed(() => guestGroups.value.find(g => g.id === linkGroupId.value))

const loadPlantillas = async () => {
  const result = await mensajeApi.getPlantillas()
  plantillas.value = result.length ? result : [nuevaPlantilla('Predeterminada', DEFAULT_MENSAJE_PLANTILLA)]
  if (!selectedPlantillaId.value) selectedPlantillaId.value = plantillas.value[0]?.id || null
}

const openLinkDialog = () => {
  showLinkDialog.value = true
  linkCopied.value = false
  if (!plantillas.value.length) loadPlantillas()
}

const mensajeVars = (grupo) => ({
  codigo: grupo?.codigo || '',
  encargado: grupo?.encargado || '',
  cantidad: String(grupo?.cantidad ?? ''),
  autos: String(grupo?.autos ?? ''),
  fechas: grupo?.fechas || '',
  linkEstudios: linkUrl.value,
})

const updateMessageDraft = () => {
  if (!linkGroup.value) return
  const plantilla = plantillas.value.find((p) => p.id === selectedPlantillaId.value)
  messageDraft.value = renderMensajePlantilla(plantilla?.texto, mensajeVars(linkGroup.value))
}

watch(linkGroupId, async (groupId) => {
  if (!groupId) return
  linkLoading.value = true
  linkCopied.value = false
  try {
    let result = await estudiosApi.getEstudioLink(groupId)
    if (!result.token) {
      result = await estudiosApi.createEstudioLink(groupId)
    }
    linkUrl.value = result.token ? estudiosApi.getPublicUrl(result.token) : ''
  } finally {
    linkLoading.value = false
  }
  updateMessageDraft()
})

watch(selectedPlantillaId, updateMessageDraft)

const copyLink = () => {
  navigator.clipboard.writeText(linkUrl.value)
  linkCopied.value = true
  setTimeout(() => { linkCopied.value = false }, 2000)
}

const openLinkInNewTab = () => {
  if (linkUrl.value) window.open(linkUrl.value, '_blank', 'noopener')
}

const sendLinkWhatsapp = () => {
  const phone = String(linkGroup.value?.whatsapp || '').replace(/\D/g, '')
  const text = encodeURIComponent(messageDraft.value || linkUrl.value)
  const url = phone
    ? `https://wa.me/${phone}?text=${text}`
    : `https://api.whatsapp.com/send?text=${text}`
  window.open(url, '_blank', 'noopener')
}

// ─── Enlace individual por estudio, orientado al publicador al que se le asignó ─
const showAsignacionLinkDialog = ref(false)
const asignacionLinkUrl = ref('')
const asignacionLinkLoading = ref(false)
const asignacionLinkCopied = ref(false)

const openAsignacionLinkDialog = async (estudio) => {
  showAsignacionLinkDialog.value = true
  asignacionLinkCopied.value = false
  asignacionLinkLoading.value = true
  try {
    let result = await estudiosApi.getEstudioAsignacionLink(estudio.id)
    if (!result.token) {
      result = await estudiosApi.createEstudioAsignacionLink(estudio.id)
    }
    asignacionLinkUrl.value = result.token ? estudiosApi.getPublicUrlAsignacion(result.token) : ''
  } finally {
    asignacionLinkLoading.value = false
  }
}

const copyAsignacionLink = () => {
  navigator.clipboard.writeText(asignacionLinkUrl.value)
  asignacionLinkCopied.value = true
  setTimeout(() => { asignacionLinkCopied.value = false }, 2000)
}

const openAsignacionLinkInNewTab = () => {
  if (asignacionLinkUrl.value) window.open(asignacionLinkUrl.value, '_blank', 'noopener')
}

const sendAsignacionLinkWhatsapp = () => {
  const text = encodeURIComponent(asignacionLinkUrl.value)
  window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank', 'noopener')
}

// ─── Enviar WhatsApp con plantilla (estudiante, publicador, asignado, grupos sin informar) ─
const estudioMensajeVars = (estudio) => ({
  estudiante: estudio?.estudiante || '',
  telefonoEstudiante: estudio?.estudianteWhatsapp || '',
  publicador: estudio?.publicador || '',
  telefonoPublicador: estudio?.whatsapp || '',
  asignadoA: estudio?.asignadoA || '',
  telefonoAsignadoA: estudio?.asignadoAWhatsapp || '',
  direccionEstudio: estudio?.direccion || '',
  fechaInicioEstudio: estudio?.fechaInicio || '',
  territorioEstudio: estudio?.territorioNombre || '',
})

// ─── Copiar mensaje con los datos del estudio (para pegar en WhatsApp) ──────
const showCopiarDialog = ref(false)
const copiarEstudio = ref(null)
const copiarTexto = ref('')
const copiarHecho = ref(false)

const armarMensajeCopia = (e) => {
  if (!e) return ''
  const lineas = []
  const nombre = e.estudiante || e.publicador
  if (nombre) lineas.push(`*Nombre:* ${nombre}`)
  const telefono = e.estudianteWhatsapp || (!e.estudiante ? e.whatsapp : '')
  if (telefono) lineas.push(`*Teléfono:* ${telefono}`)
  if (e.direccion) lineas.push(`*Dirección:* ${e.direccion}`)
  if (e.lat != null && e.lng != null) lineas.push(`*Ubicación:* https://www.google.com/maps?q=${e.lat},${e.lng}`)
  if (e.observaciones) lineas.push('', '*Observaciones:*', e.observaciones)
  return lineas.join('\n')
}

const abrirCopiarMensaje = (estudio) => {
  copiarEstudio.value = estudio
  copiarTexto.value = armarMensajeCopia(estudio)
  copiarHecho.value = false
  showCopiarDialog.value = true
}

// navigator.clipboard sólo existe en contextos seguros (https/localhost);
// si no está, se copia con un textarea temporal.
const copiarAlPortapapeles = async (texto) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(texto)
      return true
    } catch { /* se intenta el método alternativo */ }
  }
  const ta = document.createElement('textarea')
  ta.value = texto
  ta.setAttribute('readonly', '')
  ta.style.position = 'fixed'
  ta.style.opacity = '0'
  document.body.appendChild(ta)
  ta.select()
  const ok = document.execCommand('copy')
  ta.remove()
  return ok
}

const copiarMensaje = async () => {
  const ok = await copiarAlPortapapeles(copiarTexto.value)
  if (!ok) {
    toast.add({ severity: 'error', summary: 'No se pudo copiar', detail: 'Seleccioná el texto y copialo a mano.', life: 4000 })
    return
  }
  copiarHecho.value = true
  setTimeout(() => { copiarHecho.value = false }, 2000)
}

const showWaDialog = ref(false)
const waTelefono = ref('')
const waVars = ref({})
const waPlantillaId = ref(null)
const waMensaje = ref('')

const updateWaMensaje = () => {
  const plantilla = plantillas.value.find((p) => p.id === waPlantillaId.value)
  waMensaje.value = plantilla ? renderMensajePlantilla(plantilla.texto, waVars.value) : ''
}

watch(waPlantillaId, updateWaMensaje)

const abrirEnviarWhatsapp = async (telefono, vars) => {
  if (!plantillas.value.length) await loadPlantillas()
  waTelefono.value = telefono
  waVars.value = vars
  waPlantillaId.value = plantillas.value[0]?.id || null
  updateWaMensaje()
  showWaDialog.value = true
}

// Para un grupo sin informar, el dato más útil de la plantilla es el enlace para
// cargar estudios: lo resolvemos (o creamos) antes de abrir el compositor.
const abrirEnviarWhatsappGrupo = async (grupo) => {
  let result = await estudiosApi.getEstudioLink(grupo.id)
  if (!result.token) result = await estudiosApi.createEstudioLink(grupo.id)
  const linkEstudios = result.token ? estudiosApi.getPublicUrl(result.token) : ''
  await abrirEnviarWhatsapp(grupo.whatsapp, { ...mensajeVars(grupo), linkEstudios })
}

const enviarWaCompuesto = () => {
  const phone = String(waTelefono.value || '').replace(/\D/g, '')
  const text = encodeURIComponent(waMensaje.value || '')
  const url = phone
    ? `https://wa.me/${phone}?text=${text}`
    : `https://api.whatsapp.com/send?text=${text}`
  window.open(url, '_blank', 'noopener')
  showWaDialog.value = false
}

const volver = () =>
  terr.uuid
    ? route.$patch({ page: 'formTerritorio', params: { id: terr.uuid } })
    : route.$patch({ page: 'listaTerritorio' })

onMounted(() => {
  loadEstudios()
  loadGuestGroups()
  loadZonas()
  loadPlantillas()
})
</script>
